(() => {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';
  const debounce = (fn, wait = 180) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  };

  const lockBody = (locked) => document.body.classList.toggle('is-locked', locked);
  const showToast = (message) => {
    const toast = document.querySelector('[data-toast]');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const openLayer = (layer, trigger) => {
    if (!layer) return;
    layer.hidden = false;
    requestAnimationFrame(() => layer.classList.add('is-open'));
    trigger?.setAttribute('aria-expanded', 'true');
    lockBody(true);
    const focusable = layer.querySelector('input, button, a, select, textarea, [tabindex]:not([tabindex="-1"])');
    setTimeout(() => focusable?.focus(), 50);
  };

  const closeLayer = (layer, trigger) => {
    if (!layer) return;
    layer.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    setTimeout(() => { layer.hidden = true; }, 220);
    lockBody(false);
    trigger?.focus?.();
  };

  function initHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header || header.dataset.ready === 'true') return;
    header.dataset.ready = 'true';
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 36);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });

    const mobileMenu = document.querySelector('[data-mobile-menu]');
    const mobileTrigger = document.querySelector('[data-mobile-menu-open]');
    mobileTrigger?.addEventListener('click', () => openLayer(mobileMenu, mobileTrigger));
    mobileMenu?.querySelectorAll('[data-mobile-menu-close]').forEach((button) => button.addEventListener('click', () => closeLayer(mobileMenu, mobileTrigger)));

    const searchModal = document.querySelector('[data-predictive-search-modal]');
    const searchTrigger = document.querySelector('[data-search-open]');
    searchTrigger?.addEventListener('click', () => openLayer(searchModal, searchTrigger));
    searchModal?.querySelectorAll('[data-search-close]').forEach((button) => button.addEventListener('click', () => closeLayer(searchModal, searchTrigger)));

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (mobileMenu && !mobileMenu.hidden) closeLayer(mobileMenu, mobileTrigger);
      if (searchModal && !searchModal.hidden) closeLayer(searchModal, searchTrigger);
    });
  }

  function initReveal(scope = document) {
    const elements = [...scope.querySelectorAll('.reveal:not([data-reveal-ready])')];
    elements.forEach((element) => { element.dataset.revealReady = 'true'; });
    if (!elements.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    elements.forEach((element) => observer.observe(element));
  }

  function initQuantitySelectors(scope = document) {
    scope.querySelectorAll('[data-quantity-selector]').forEach((selector) => {
      if (selector.dataset.ready) return;
      selector.dataset.ready = 'true';
      const input = selector.querySelector('input');
      selector.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const minimum = Number(input.min || 1);
        input.value = Math.max(minimum, Number(input.value || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      selector.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const maximum = Number(input.max || Infinity);
        input.value = Math.min(maximum, Number(input.value || 1) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function initTabs(scope = document) {
    scope.querySelectorAll('[data-tabs]').forEach((tabs) => {
      if (tabs.dataset.ready) return;
      tabs.dataset.ready = 'true';
      const buttons = [...tabs.querySelectorAll('[data-tab-button]')];
      const panels = [...tabs.querySelectorAll('[data-tab-panel]')];
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const key = button.dataset.tabButton;
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', String(active));
            item.tabIndex = active ? 0 : -1;
          });
          panels.forEach((panel) => {
            const active = panel.dataset.tabPanel === key;
            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
            if (active) panel.querySelectorAll('.reveal').forEach((item) => item.classList.add('is-visible'));
          });
        });
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const index = buttons.indexOf(button);
          const next = event.key === 'ArrowRight' ? (index + 1) % buttons.length : (index - 1 + buttons.length) % buttons.length;
          buttons[next].focus();
          buttons[next].click();
        });
      });
    });
  }

  function initCarousels(scope = document) {
    scope.querySelectorAll('[data-carousel]').forEach((carousel) => {
      if (carousel.dataset.ready) return;
      carousel.dataset.ready = 'true';
      const viewport = carousel.querySelector('[data-carousel-viewport]');
      const track = carousel.querySelector('[data-carousel-track]');
      const slides = [...track?.children || []];
      const dots = carousel.querySelector('[data-carousel-dots]');
      if (!viewport || !track || slides.length < 2) return;
      let index = 0;
      let startX = 0;
      let deltaX = 0;

      const visibleCount = () => window.innerWidth <= 520 ? 1 : window.innerWidth <= 1100 ? 2 : 3;
      const maxIndex = () => Math.max(0, slides.length - visibleCount());
      const update = () => {
        index = Math.min(index, maxIndex());
        const gap = parseFloat(getComputedStyle(track).gap) || 0;
        const slideWidth = slides[0].getBoundingClientRect().width;
        track.style.transform = `translate3d(${-index * (slideWidth + gap)}px,0,0)`;
        dots?.querySelectorAll('button').forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      };

      if (dots) {
        for (let i = 0; i <= maxIndex(); i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = `carousel-dot${i === 0 ? ' is-active' : ''}`;
          dot.addEventListener('click', () => { index = i; update(); });
          dots.append(dot);
        }
      }
      carousel.querySelector('[data-carousel-prev]')?.addEventListener('click', () => { index = Math.max(0, index - 1); update(); });
      carousel.querySelector('[data-carousel-next]')?.addEventListener('click', () => { index = Math.min(maxIndex(), index + 1); update(); });
      viewport.addEventListener('pointerdown', (event) => { startX = event.clientX; deltaX = 0; viewport.setPointerCapture(event.pointerId); });
      viewport.addEventListener('pointermove', (event) => { if (!startX) return; deltaX = event.clientX - startX; });
      viewport.addEventListener('pointerup', () => {
        if (Math.abs(deltaX) > 50) index = deltaX < 0 ? Math.min(maxIndex(), index + 1) : Math.max(0, index - 1);
        startX = 0; deltaX = 0; update();
      });
      window.addEventListener('resize', debounce(update, 120));
      update();
    });
  }

  function initProductGalleries(scope = document) {
    scope.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-gallery-track]');
      const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumbnail]')];
      const current = gallery.querySelector('[data-gallery-current]');
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let touchStart = 0;

      const goTo = (nextIndex) => {
        if (!slides.length) return;
        index = (nextIndex + slides.length) % slides.length;
        if (track) track.style.transform = `translate3d(${-index * 100}%,0,0)`;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          slide.querySelectorAll('video').forEach((video) => { if (!active) video.pause(); });
        });
        thumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === index;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', String(active));
        });
        if (current) current.textContent = String(index + 1);
      };

      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goTo(index - 1));
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => goTo(index + 1));
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.targetIndex))));
      gallery.querySelector('.product-gallery__viewport')?.addEventListener('touchstart', (event) => { touchStart = event.touches[0].clientX; }, { passive: true });
      gallery.querySelector('.product-gallery__viewport')?.addEventListener('touchend', (event) => {
        const delta = event.changedTouches[0].clientX - touchStart;
        if (Math.abs(delta) > 50) goTo(index + (delta < 0 ? 1 : -1));
      }, { passive: true });
      gallery._goToMedia = (mediaId) => {
        const mediaIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
        if (mediaIndex >= 0) goTo(mediaIndex);
      };
      goTo(index);
    });
  }

  function initVariantSelectors(scope = document) {
    scope.querySelectorAll('[data-variant-select]').forEach((select) => {
      if (select.dataset.ready) return;
      select.dataset.ready = 'true';
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const container = select.closest('[data-product-root], [data-featured-product]');
        const price = container?.querySelector('[data-product-price], [data-featured-price]');
        const addButton = container?.querySelector('[data-add-button]');
        const addLabel = addButton?.querySelector('[data-add-label]');
        if (price && option.dataset.price) {
          price.innerHTML = `<span>${option.dataset.price}</span>${option.dataset.comparePrice ? `<s>${option.dataset.comparePrice}</s>` : ''}`;
        }
        const available = option.dataset.available === 'true';
        if (addButton) addButton.disabled = !available;
        if (addLabel) addLabel.textContent = available ? 'Ajouter au panier' : 'Épuisé';
        const gallery = container?.querySelector('[data-product-gallery]');
        if (gallery && option.dataset.mediaId) gallery._goToMedia?.(option.dataset.mediaId);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      });
    });
  }

  async function addToCart(form) {
    const submit = form.querySelector('button[type="submit"]');
    submit?.classList.add('is-loading');
    submit?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Impossible d’ajouter ce produit.');
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      showToast(`${result.product_title || 'Produit'} ajouté au panier.`);
    } catch (error) {
      showToast(error.message);
    } finally {
      submit?.classList.remove('is-loading');
      submit?.removeAttribute('aria-busy');
    }
  }

  function initAjaxForms(scope = document) {
    scope.querySelectorAll('[data-ajax-product-form], [data-quick-add-form]').forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        addToCart(form);
      });
    });
  }

  function initSort(scope = document) {
    scope.querySelectorAll('[data-sort-by]').forEach((select) => {
      if (select.dataset.ready) return;
      select.dataset.ready = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });
  }

  function initParallax() {
    if (document.documentElement.dataset.parallaxReady === 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.innerWidth < 800) return;
    const elements = [...document.querySelectorAll('[data-parallax-element]')];
    document.documentElement.dataset.parallaxReady = 'true';
    if (!elements.length) return;
    let ticking = false;
    const update = () => {
      const center = window.innerHeight / 2;
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const speed = Number(element.dataset.parallaxSpeed || 0.15);
        const offset = (rect.top + rect.height / 2 - center) * speed;
        element.style.transform = `translate3d(0,${offset * -0.18}px,0)`;
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  function initAll(scope = document) {
    initHeader();
    initReveal(scope);
    initQuantitySelectors(scope);
    initTabs(scope);
    initCarousels(scope);
    initProductGalleries(scope);
    initVariantSelectors(scope);
    initAjaxForms(scope);
    initSort(scope);
    initParallax();
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  window.LMDS = { initAll, showToast, openLayer, closeLayer };
})();
